import React, { useState } from 'react';
import getApiBase from './getApiBase';

const PlaceIdRepairModal = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [shopData, setShopData] = useState(null);
  const [repairResult, setRepairResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('search');

  if (!isOpen) return null;

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a Shop ID or Name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const isNumeric = /^\d+$/.test(searchQuery.trim());
      
      let response;
      if (isNumeric) {
        // Search by ID
        response = await fetch(`${getApiBase()}/api/admin/shops/${searchQuery}`, {
          credentials: 'include'
        });
      } else {
        // Search by name
        response = await fetch(`${getApiBase()}/api/admin/shops?search=${encodeURIComponent(searchQuery)}`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.shops && data.shops.length > 0) {
            // Take first result
            const shopResponse = await fetch(`${getApiBase()}/api/admin/shops/${data.shops[0].id}`, {
              credentials: 'include'
            });
            if (shopResponse.ok) {
              response = shopResponse;
            } else {
              throw new Error('No shops found with that name');
            }
          } else {
            throw new Error('No shops found with that name');
          }
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to search shop');
      }

      const data = await response.json();
      setShopData(data);
      setStep('confirm');
    } catch (err) {
      setError(err.message || 'Failed to search shop');
    } finally {
      setLoading(false);
    }
  };

  const handleRepair = async () => {
    if (!shopData) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${getApiBase()}/api/admin/repair-shop-place-id/${shopData.id}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to repair place_id');
      }

      const data = await response.json();
      setRepairResult(data);
      setStep('success');
    } catch (err) {
      setError(err.message || 'Failed to repair place_id');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setShopData(null);
    setRepairResult(null);
    setError(null);
    setStep('search');
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            margin: 0,
            fontFamily: 'Poppins, sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>🔧</span>
            Repair Shop place_id
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#9ca3af',
              cursor: 'pointer',
              padding: '4px 8px'
            }}
            onMouseOver={(e) => e.target.style.color = '#6b7280'}
            onMouseOut={(e) => e.target.style.color = '#9ca3af'}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {step === 'search' && (
            <>
              <p style={{
                color: '#6b7280',
                marginBottom: '16px',
                fontFamily: 'Poppins, sans-serif'
              }}>
                Search for a shop by ID or name to check and repair its Google Place ID.
              </p>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Shop ID or Name"
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Poppins, sans-serif'
                  }}
                />
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: loading ? '#9ca3af' : '#2563eb',
                    color: 'white',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: 'Poppins, sans-serif'
                  }}
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>

              {error && (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#991b1b',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}
            </>
          )}

          {step === 'confirm' && shopData && (
            <>
              <div style={{
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  fontWeight: 600,
                  marginBottom: '12px',
                  fontFamily: 'Poppins, sans-serif'
                }}>
                  Current Shop Data:
                </h3>
                
                <div style={{ fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 500 }}>ID:</span> {shopData.id}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 500 }}>Name:</span> {shopData.shop_name}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 500 }}>Address:</span>{' '}
                    {shopData.address?.street_address}, {shopData.address?.city},{' '}
                    {shopData.address?.state} {shopData.address?.zip_code}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 500 }}>Phone:</span> {shopData.phone_number || shopData.google_phone || 'N/A'}
                  </div>
                  
                  <div style={{
                    paddingTop: '12px',
                    borderTop: '1px solid #e5e7eb',
                    marginTop: '12px'
                  }}>
                    <div style={{
                      color: !shopData.place_id ? '#dc2626' : '#374151',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontWeight: 500 }}>
                        {!shopData.place_id ? '⚠️ ' : ''}place_id:
                      </span>{' '}
                      {shopData.place_id || 'NULL'}
                    </div>
                    <div style={{
                      color: !shopData.latitude || !shopData.longitude ? '#dc2626' : '#374151',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontWeight: 500 }}>
                        {!shopData.latitude || !shopData.longitude ? '⚠️ ' : ''}Coordinates:
                      </span>{' '}
                      {shopData.latitude || 'NULL'}, {shopData.longitude || 'NULL'}
                    </div>
                    <div>
                      <span style={{ fontWeight: 500 }}>Rating:</span> {shopData.rating || 0}
                    </div>
                  </div>
                </div>
              </div>

              {(!shopData.place_id || !shopData.latitude || !shopData.longitude) && (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fefce8',
                  border: '1px solid #fde047',
                  borderRadius: '6px',
                  color: '#854d0e',
                  marginBottom: '16px',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px'
                }}>
                  ⚠️ This shop has missing or potentially incorrect location data.
                </div>
              )}

              {error && (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#991b1b',
                  marginBottom: '16px',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setStep('search')}
                  style={{
                    padding: '12px 24px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontFamily: 'Poppins, sans-serif'
                  }}
                >
                  Back
                </button>
                <button
                  onClick={handleRepair}
                  disabled={loading}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: loading ? '#9ca3af' : '#2563eb',
                    color: 'white',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: 'Poppins, sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {loading ? (
                    <>
                      <span>⏳</span>
                      Repairing...
                    </>
                  ) : (
                    <>
                      🔧 Repair place_id
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {step === 'success' && repairResult && (
            <>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#059669',
                  marginBottom: '8px',
                  fontFamily: 'Poppins, sans-serif'
                }}>
                  place_id Repaired Successfully!
                </h3>
                <p style={{
                  color: '#6b7280',
                  fontFamily: 'Poppins, sans-serif'
                }}>
                  Shop {repairResult.shopId} has been updated with the correct location data.
                </p>
              </div>

              <div style={{
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px'
                }}>
                  <div>
                    <h4 style={{
                      fontWeight: 600,
                      marginBottom: '8px',
                      color: '#dc2626',
                      fontFamily: 'Poppins, sans-serif'
                    }}>
                      Before:
                    </h4>
                    <div style={{ fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontWeight: 500 }}>place_id:</span>
                        <div style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          wordBreak: 'break-all',
                          marginTop: '4px'
                        }}>
                          {repairResult.oldPlaceId || 'NULL'}
                        </div>
                      </div>
                      <div>
                        <span style={{ fontWeight: 500 }}>Coordinates:</span>
                        <div style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          marginTop: '4px'
                        }}>
                          {repairResult.oldCoords.lat || 'NULL'}, {repairResult.oldCoords.lng || 'NULL'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 style={{
                      fontWeight: 600,
                      marginBottom: '8px',
                      color: '#059669',
                      fontFamily: 'Poppins, sans-serif'
                    }}>
                      After:
                    </h4>
                    <div style={{ fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontWeight: 500 }}>place_id:</span>
                        <div style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          wordBreak: 'break-all',
                          marginTop: '4px'
                        }}>
                          {repairResult.newPlaceId}
                        </div>
                      </div>
                      <div>
                        <span style={{ fontWeight: 500 }}>Coordinates:</span>
                        <div style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          marginTop: '4px'
                        }}>
                          {repairResult.newCoords.lat}, {repairResult.newCoords.lng}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}>
                    <span style={{ fontWeight: 500 }}>Matched by:</span>{' '}
                    <span style={{ color: '#2563eb' }}>{repairResult.matchedBy}</span>
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    marginTop: '8px',
                    fontFamily: 'Poppins, sans-serif'
                  }}>
                    ✅ Reviews have been refreshed with correct location data
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleClose}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: 'Poppins, sans-serif'
                  }}
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaceIdRepairModal;

